import React, {useEffect, useMemo, useState} from 'react';
import '../styles/pages/user-registration.css';
import Button from "../components/Button";
import List from '../components/List';
import '../styles/pages/record-info.css';
import Select from '../components/Select';
import MantinePagination from '../components/MantinePagination';
import MantineSelectBox from '../components/MantineSelectBox';
import {useDaumPostcodePopup} from 'react-daum-postcode';
import {apiRequest} from "../utils/api";
import {Modal, PasswordInput} from '@mantine/core';
import MantineDatePicker from '../components/MantineDatePicker';
import ResetButton from '../components/ResetButton';
import dayjs from "dayjs";
import Alert from '../components/Alert';
import filterIcon from '../assets/icons/filter-icon.svg';
import ToggleButton from '../components/ToggleButton';

const UserRegistration = () => {
    const defaultForm = {
        REG_DTM: new Date().toISOString().slice(0, 10),
        NAME: '',
        RNMCNO: '',
        ADDR: '',
        CODE: '',
        DETAIL_ADDR: '',
        PHONE: '',
        USERID: '',
        PASSWORD: '',
        PASSWORD_CONFIRM: '',
        STATUS: 'Y',
    };
    const [formData, setFormData] = useState(defaultForm);
    const [userList, setUserList] = useState([]);
    const headers = ['일시', '이름', '상태', '연락처', '등급', '보기'];
    const userListColumnWidths = ['15%', '15%', '10%', '20%', '20%', '20%', '20%'];
    const [idAvailable, setIdAvailable] = useState(null);
    const open = useDaumPostcodePopup();
    const [error, setError] = useState('');
    const [selectedUserID, setSelectedUserID] = useState(null);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [checkedUserId, setCheckedUserId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [alertOpen, setAlertOpen] = useState({ isOpen: false, message: ''});
    const [mobileFilterModalOpen, setMobileFilterModalOpen] = useState(false);
    
    //필터링
    const [statusFilter, setStatusFilter] = useState('');
    const [userName, setUserName] = useState('');

    const today = new Date();
    const oneWeekAgo = new Date(today);
    oneWeekAgo.setDate(today.getDate() - 7);

    const [startDate, setStartDate] = useState(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));

    const pageData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return userList.slice(startIndex, endIndex);
    }, [currentPage, itemsPerPage, userList]);

    const totalPages = Math.ceil(userList.length / itemsPerPage);

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleChange = (field, value) => {
        if (field === 'PHONE') {
            let numbers = value.replace(/[^0-9]/g, '');
            if (numbers.length > 11) {
                numbers = numbers.slice(0, 11);
            }
            let formatted = numbers;
            if (numbers.length > 3 && numbers.length <= 7) {
                formatted = numbers.slice(0, 3) + '-' + numbers.slice(3);
            } else if (numbers.length > 7) {
                formatted = numbers.slice(0, 3) + '-' + numbers.slice(3, 7) + '-' + numbers.slice(7);
            }
            setFormData({...formData, [field]: formatted});
        } else if (field === 'USERID') {
            let filteredValue = value.replace(/[^a-zA-Z0-9]/g, '');
            if (filteredValue.length > 10) {
                filteredValue = filteredValue.slice(0, 10);
            }
            if (filteredValue !== formData.USERID) {
                setIdAvailable(null);
                setCheckedUserId(null);
            } 
            setFormData({...formData, [field]: filteredValue});
        } else {
            setFormData({...formData, [field]: value});
        }
    };

    const handleSsnChange = (e) => {
        let value = e.target.value.replace(/[^0-9]/g, '');
        if (value.length > 6) value = value.slice(0, 6) + '-' + value.slice(6, 13);
        handleChange('RNMCNO', value);
    };

    const handleCheckId = async () => {
        // 빈값 체크
        if (!formData.USERID || formData.USERID.trim() === '') {
            setIdAvailable(false);
            setCheckedUserId(null);
            return;
        }

        const res = await apiRequest('/checkID', 'POST', { USERID: formData.USERID });
        if (res.available) {
            setCheckedUserId(formData.USERID);
            setIdAvailable(true);
        } else {
            setIdAvailable(false);
            setCheckedUserId(null);
        }
    };

    const handleComplete = (data) => {
        const fullAddress = data.roadAddress || data.jibunAddress;
        setFormData(prev => ({
            ...prev,
            ADDR: fullAddress,
            CODE: data.zonecode
        }));
    };

    const handleSearchAddress = () => {
        open({onComplete: handleComplete});
    };

    const handleSave = async () => {
        const password = formData.PASSWORD;
        const confirmPassword = formData.PASSWORD_CONFIRM;

        if (formData.USERID !== checkedUserId) {
            showAlert("아이디 중복 확인을 해주세요.");
            return;
        }
        // 정규식: 영문 + 숫자 + 특수문자 포함, 8자 이상
        const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*()_\-+=])[A-Za-z\d!@#$%^&*()_\-+=]{8,}$/;

        if (password !== confirmPassword) {
            showAlert("비밀번호가 일치하지 않습니다.");
            return;
        }

        if (!passwordRegex.test(password)) {
            showAlert("비밀번호는 영문, 숫자, 특수문자를 포함해 8자 이상이어야 합니다.");
            return;
        }

        try {
            const saveUserData = await apiRequest('/regist', 'POST', formData);
            console.log("saveUserData",saveUserData);
            if (saveUserData.success) {
                showAlert("회원 저장 완료되었습니다.");
                fetchUsers();
                handleReset();
            } else {
                setError('회원 저장에 실패 하였습니다.');
            }
        } catch (err) {
            setError('회원 저장에 실패 하였습니다.');
        }
    };

    const showAlert = (message) => { setAlertOpen({ isOpen: true, message });};
    const closeAlert = () => {setAlertOpen({ isOpen: false,  message: '' });};

    const handleReset = () => {
        setFormData(defaultForm);
        setIdAvailable(null);
        setIsReadOnly(false);
        setSelectedUserID(null);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (value) => {
        setItemsPerPage(parseInt(value));
        setCurrentPage(1);
    };

    const fetchUsers = async () => {
        try {
            const res = await apiRequest('/users', 'POST', {
                startDate,
                endDate,
                status: statusFilter,
                userName,
            });

            const refinedData = res.map(user => {
                return {
                    REG_DTM: user.REG_DTM?.substring(0, 10),
                    USER: user.NAME,
                    STATUS: user.STATUS === 'Y' ? '정상' : '제한',
                    PHONE: user.PHONE,
                    STATE: user.STATE === 'U' ? '일반회원' : '관리자',
                    _fullUser: user,
                    VIEW: null
                };
            });

            const finalData = refinedData.map(user => ({
                ...user,
                VIEW: <Button style={{ background: '#F9F9F9', color: '#3d3d3d', padding: '5px 20px'}} onClick={() => handleView(user._fullUser)}>보기</Button>
            }));

            setUserList(finalData);
        } catch (err) {
            console.error("회원 목록 불러오기 실패", err);
        }
    };

    const handleDelete = async () => {
        if (!selectedUserID) return showAlert("선택된 사용자가 없습니다.");

        try {
            await apiRequest('/user-delete', 'POST', {id: selectedUserID});
            showAlert("사용자가 삭제되었습니다.");
            fetchUsers();
            handleReset();
        } catch (err) {
            console.error("유저 삭제 오류", err);
            showAlert("삭제 실패");
        }
    };

    const handleUpdate = async () => {
        if (!selectedUserID) return showAlert("수정할 사용자가 선택되지 않았습니다.");

        try {
            const updatedData = {
                ID: selectedUserID,
                NAME: formData.NAME,
                RNMCNO: formData.RNMCNO,
                ADDR: formData.ADDR,
                CODE: formData.CODE,
                DETAIL_ADDR: formData.DETAIL_ADDR,
                PHONE: formData.PHONE,
                STATUS: formData.STATUS,
                REG_DTM: formData.REG_DTM,
                PASSWORD: formData.PASSWORD,
            };

            await apiRequest('/user-update', 'POST', updatedData);
            showAlert("사용자 정보가 수정되었습니다.");
            fetchUsers();
        } catch (err) {
            console.error("유저 수정 오류", err);
            showAlert("수정 실패");
        }
    };

    const handleView = (user) => {
        setSelectedUserID(user.ID);

        setFormData({
            REG_DTM: user.REG_DTM?.substring(0, 10) || '',
            NAME: user.NAME || '',
            RNMCNO: user.RNMCNO || '',
            ADDR: user.ADDR || '',
            CODE: user.CODE || '',
            DETAIL_ADDR: user.DETAIL_ADDR || '',
            PHONE: user.PHONE || '',
            USERID: user.USERID || '',
            PASSWORD: user.PASSWORD || '',
            PASSWORD_CONFIRM: '',
            STATUS: user.STATUS || 'Y'
        });

        setIsReadOnly(true);
    };

    const handleMobileFilterSearch = () => {
        fetchUsers();
        setMobileFilterModalOpen(false);
    };

    const countOptions = [
        {value: '10', label: '10개'},
        {value: '20', label: '20개'},
        {value: '50', label: '50개'},
        {value: '100', label: '100개'},
    ];

    return (
        <div className="page-content">
            <div className='left-section'>
                <div className="reset-button-wrapper">
                    <ResetButton onClick={handleReset} size="small"/>
                </div>
                <div className="small-margin-section"/>

                <div className="input-wrapper gmarket-font">
                    <p>등록일</p>
                    <MantineDatePicker
                        value={formData.REG_DTM}
                        onChange={date => handleChange('REG_DTM', date)}
                    />
                </div>
                <div className="input-wrapper gmarket-font">
                    <p>이름</p>
                    <input className='gray-input' value={formData.NAME} maxLength={15}
                           onChange={e => handleChange('NAME', e.target.value)}/>
                </div>
                <div className="input-wrapper gmarket-font">
                    <p>주민번호</p>
                    <input className='gray-input' value={formData.RNMCNO} onChange={handleSsnChange}/>
                </div>
                <div className="input-wrapper gmarket-font">
                    <p>주소</p>
                    <input
                        className='gray-input'
                        type="text"
                        readOnly
                        value={formData.CODE || ''}
                        placeholder="우편번호"
                    />
                    <Button className='confirm-button' onClick={handleSearchAddress}>주소 검색</Button>
                </div>
                <div className="input-wrapper gmarket-font">
                    <p></p>
                    <input
                        className='gray-input'
                        type="text"
                        name="ADDR"
                        readOnly
                        value={formData.ADDR || ''}
                        placeholder="주소"
                    />

                </div>
                <div className="input-wrapper gmarket-font">
                    <p></p>
                    <input
                        className='gray-input'
                        type="text"
                        name="DETAIL_ADDR"
                        value={formData.DETAIL_ADDR || ''}
                        onChange={e => handleChange('DETAIL_ADDR', e.target.value)}
                        placeholder="상세주소"
                    />

                </div>

                <div className="input-wrapper gmarket-font">
                    <p>연락처</p>
                    <input className='gray-input' value={formData.PHONE} onChange={e => handleChange('PHONE', e.target.value)}maxLength={13}/>
                </div>
                <div className="input-wrapper gmarket-font">
                    <p>아이디</p>
                    <input className='gray-input' value={formData.USERID} onChange={e => handleChange('USERID', e.target.value)} maxLength={10} readOnly={isReadOnly}/>
                    <Button className='confirm-button' onClick={handleCheckId}>중복확인</Button>
                </div>

                <div className="input-wrapper">      
                    <p></p>
                    {(idAvailable === true || idAvailable === false) && (
                        <div style={{
                            display: 'flex', 
                            marginBottom: '14px',
                            backgroundColor: idAvailable ? '#F1F9EE' : '#FFE1E1',
                            color: idAvailable ? 'green' : 'red',
                            padding: '8px',
                            borderRadius: '5px',
                            width: '100%',        
                        }}>
                            {idAvailable === true && <span>사용 가능한 아이디입니다.</span>}
                            {idAvailable === false && <span>아이디 중복 확인을 진행해 주세요.</span>}
                        </div>
                    )}
                </div> 

                <form onSubmit={(e) => e.preventDefault()}>
                    <div className="input-wrapper gmarket-font">
                        <p>비밀번호</p>
                        <PasswordInput value={formData.PASSWORD} onChange={e => handleChange('PASSWORD', e.target.value)} className="password-input" maxLength={15} />
                    </div>
                    <div className="input-wrapper gmarket-font">
                        <p>비밀번호확인</p>
                        <PasswordInput value={formData.PASSWORD_CONFIRM} onChange={e => handleChange('PASSWORD_CONFIRM', e.target.value)} className="password-input"  maxLength={15} />
                    </div>
                </form>

                <div className="toggle-section">
   <p className="title-text">로그인상태</p>
  <ToggleButton
    value={formData.STATUS}
    onChange={(value) => handleChange('STATUS', value)}
    options={[
      { value: 'Y', label: '승인' },
      { value: 'N', label: '제한' }
    ]}
  />                 
</div>

                <div className='small-margin-section'></div>

                <div className="button-wrapper">
                    {!selectedUserID && (<Button className="bottom-button" onClick={handleSave}>저장</Button>)}
                    {selectedUserID && (<Button className="bottom-button" onClick={handleUpdate}>수정</Button>)}
                    <Button className="bottom-button" onClick={handleDelete}>삭제</Button>
                </div>
            </div>

            <div className='right-section'>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '16px'
                }}>
                    <p className="title-text gmarket-font">USER정보</p>
                    <img
                        src={filterIcon}
                        alt="필터아이콘"
                        className="filter-icon"
                        onClick={() => setMobileFilterModalOpen(true)}
                    />
                </div>

                <div className="top-section desktop-filter">
                    <div className='filter-section2'>
                        <div className="date-filter-wrapper">
                        <MantineDatePicker value={startDate} onChange={setStartDate}/>
                        <span style={{margin: '10px 8px'}}>-</span>
                        <MantineDatePicker value={endDate} onChange={setEndDate}/>
                    </div>
                    <div className="mobile-top-section">
                        <MantineSelectBox
                            value={statusFilter}
                            onChange={setStatusFilter}
                             withinPortal={false}
                            options={[
                                {value: '', label: '전체'},
                                {value: 'Y', label: '정상'},
                                {value: 'N', label: '제한'},
                            ]}
                        >
                            상태
                        </MantineSelectBox>

                            <input
                                placeholder="이름 검색"
                                value={userName}
                                onChange={(e) => setUserName(e.target.value)}
                                className="site-search-input"
                                style={{ paddingLeft: '20px', width: '145px' }}
                            />
                    </div>
                </div>
                <Button width="100px" height="50px" onClick={fetchUsers}>찾기</Button>
                </div>
                
                <div>
                    <div className="total-count-text">총 유저 : {userList.length}명</div>
                </div>
                <div className="list-wrapper">
                    <List headers={headers} data={pageData} showCheckbox={false} columnWidths={userListColumnWidths}/>
                </div>
                <div className="middle-section">
                    <div></div>
                    <MantinePagination
                        total={totalPages}
                        value={currentPage}
                        onChange={handlePageChange}
                    />
                    <div className="select-wrapper">
                        <Select options={countOptions} width="107px" height="42px" value={itemsPerPage.toString()}
                                onChange={handleItemsPerPageChange}>
                        </Select>
                    </div>
                </div>
            </div>

            {mobileFilterModalOpen && (
                <Modal
                    opened={mobileFilterModalOpen}
                    onClose={() => setMobileFilterModalOpen(false)}
                    zIndex={9999}
                    centered={false} 
                    size="md"
                    withinPortal={true} 
                     styles={{  content: { position: 'fixed',  bottom: 0,   left: 0, right: 0,  margin: 0,   borderRadius: '20px 20px 0 0', transform: 'translateY(0)',  maxHeight: '100vh',overflow: 'hidden', animation: mobileFilterModalOpen ? 'slideUp 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)' : 'slideDown 0.3s ease-in' },  body: {  padding: '20px', maxHeight: '100vh', overflowY: 'auto' }, header: { display: 'none' } }} transitionProps={{  transition: 'fade', duration: 0 }} >
                    <div>
                        <div>
                            <p className='gmarket-font'>시작일</p>
                            <div>
                                <MantineDatePicker
                                    value={startDate}
                                    onChange={setStartDate}
                                    className="mobile-date-picker"
                                    comboboxProps={{
                                        withinPortal: false, 
                                        zIndex: 10002,
                                        position: 'bottom'
                                    }}
                                    popoverProps={{
                                        withinPortal: false, 
                                        zIndex: 10002,
                                        position: 'bottom' }}
                                           styles={{ levelsGroup: { display: 'flex',  justifyContent: 'center',  width: '100%' } }}
                                />
                                <p className='gmarket-font'>종료일</p>
                                <MantineDatePicker
                                    value={endDate}
                                    onChange={setEndDate}
                                    className="mobile-date-picker"
                                    comboboxProps={{
                                        withinPortal: false, 
                                        zIndex: 10002,
                                        position: 'bottom-start'
                                    }}
                                    popoverProps={{
                                        withinPortal: false, 
                                        zIndex: 10002,
                                        position: 'bottom-start',
                                        styles: { dropdown: {  width: '350px',  maxWidth: '350px'  } }  }}
                                    styles={{ levelsGroup: { display: 'flex',  justifyContent: 'center',  width: '100%' } }}
                                />
                            </div>
                        </div>

                        <div>
                            <p className='gmarket-font'>상태</p>
                            <MantineSelectBox
                                style={{width: '100%'}}
                                value={statusFilter}
                                onChange={setStatusFilter}
                                comboboxProps={{
                                    withinPortal: false,
                                    zIndex: 10002,
                                    position: 'bottom'
                                }}
                                options={[
                                    {value: '', label: '전체'},
                                    {value: 'Y', label: '정상'},
                                    {value: 'N', label: '제한'},
                                ]}
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <p className='gmarket-font' >이름 검색</p>
                                <input
                                    placeholder="이름 검색"
                                    value={userName}
                                    onChange={(e) => setUserName(e.target.value)}
                                    style={{
                                        paddingLeft: '20px',
                                        width: '100%',
                                        height: '50px',
                                        borderRadius: '3px',
                                        border: '1px solid #ABABAB',
                                        padding: '12px 12px 12px 40px',
                                        color: '#333'
                                    }}
                                />
                        </div>

                        <div style={{display: 'flex', gap: '10px'}}>
                            <Button
                                onClick={() => setMobileFilterModalOpen(false)}
                                style={{
                                    flex: 1,
                                    height: '50px',
                                    backgroundColor: '#f4f5f6',
                                    color: '#333',
                                    borderRadius: '10px'
                                }}
                            >
                                취소
                            </Button>
                            <Button
                                onClick={handleMobileFilterSearch}
                                style={{
                                    flex: 1,
                                    height: '50px',
                                    borderRadius: '10px'
                                }}
                            >
                                찾기
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            <Alert
                isOpen={alertOpen.isOpen}
                message={alertOpen.message}
                onConfirm={closeAlert}
            />
        </div>
    );
};

export default UserRegistration;